// tslint:disable:no-console
import { ApolloError, gql } from '@apollo/client'
import queryString from 'query-string'
import {
  Messaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
  getMessaging,
} from 'firebase/messaging'
import { app, auth } from './firebase'
import client from '~/client'
import config from '~/constants/config'
import { registerValidSW } from '../../registerServiceWorker'
import {
  SubscribeToTopicDocument,
  SubscribeToTopicMutation,
  SubscribeToTopicMutationVariables,
} from '~/gql/generated/graphql'
import { getStoredFeatureFlag } from '~/context/featureFlags'
import { showNotificationPermissionPrompt } from './notificationPermissionPrompt'
import { PHOTO_UPLOAD_EVENT, PHOTO_UPLOAD_TOPIC } from '~/constants/domain'

const ADD_REGISTRATION_ID_MUTATION = gql`
  mutation ADD_REGISTRATION_ID($user_id: ID!, $registration_id: ID!) {
    add_registration_id(user_id: $user_id, registration_id: $registration_id)
  }
`

// based on https://github.com/firebase/firebase-js-sdk/issues/2364#issuecomment-570820017
const getNotificationToken = async (
  messaging: Messaging,
  registration: ServiceWorkerRegistration,
  retry = 0,
): Promise<string | undefined> => {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification')
      return
    }

    const permission = Notification.permission
    if (permission === 'default') {
      // requestPermission has to come from a user gesture, so ask via a prompt
      console.log('Notification permission not yet requested, prompting.')
      showNotificationPermissionPrompt(enablePushNotifications)
      return
    }
    if (permission !== 'granted') {
      console.log(`Notification permission not granted (${permission}).`)
      return
    }

    console.log('Notification permission granted.')
    const token = await getToken(messaging, {
      vapidKey: config.VITE_FCM_KEY,
      serviceWorkerRegistration: registration,
    })
    return token
  } catch (err) {
    if (err.code === 'messaging/token-unsubscribe-failed') {
      if (retry > 2) {
        console.log(
          'Firebase messaging token unsubscribe failed many times. Aborting.',
        )
        return
      }
      console.log('Firebase messaging token unsubscribe failed. Retrying.')
      return await getNotificationToken(messaging, registration, retry + 1)
    }
    console.log('Failed to get FCM token', err)
  }
}

// Subscribe the current user's registered device tokens to the parking photo
// upload topic. The mutation is idempotent server-side. Errors are swallowed so
// a subscribe failure never affects token registration.
export const subscribeToPhotoUploadTopic = async () => {
  if (!auth.currentUser) {
    return
  }
  try {
    await client.mutate<
      SubscribeToTopicMutation,
      SubscribeToTopicMutationVariables
    >({
      mutation: SubscribeToTopicDocument,
      variables: {
        userId: auth.currentUser.uid,
        topic: PHOTO_UPLOAD_TOPIC,
      },
    })
    console.log(`Subscribed user to topic ${PHOTO_UPLOAD_TOPIC}`)
  } catch (err) {
    console.log(`Failed to subscribe to topic ${PHOTO_UPLOAD_TOPIC}`, err)
  }
}

let onMessageRegistered = false

// initialize ask for permissions
// then send the token to the backend
const registerMessaging = async () => {
  let messaging: Messaging
  if (await isSupported()) {
    messaging = getMessaging(app)
  } else {
    return null
  }

  let registration: null | ServiceWorkerRegistration = null
  // register service worker
  try {
    if (navigator.serviceWorker) {
      console.log('registering FCM service worker')
      registration = await registerValidSW(
        `/firebase-messaging-sw.js?${queryString.stringify({
          apiKey: config.FIREBASE_API_KEY,
          appId: config.FIREBASE_APP_ID,
          projectId: config.GOOGLE_PROJECT_ID,
          authDomain: config.FIREBASE_AUTH_DOMAIN,
          messagingSenderId: config.VITE_FCM_SENDER_ID,
        })}`,
      )
    }
  } catch (err) {
    console.log('registering fcm service worker failed', err)
    return
  }

  try {
    if (registration && auth.currentUser) {
      const token = await getNotificationToken(messaging, registration)

      if (!token) {
        console.log('No FCM token, skipping registration id')
        return
      }
      await client.mutate({
        mutation: ADD_REGISTRATION_ID_MUTATION,
        variables: {
          user_id: auth.currentUser.uid,
          registration_id: token,
        },
      })

      console.log('Sent registration id to api')

      // Subscribe this session's device token to the parking photo upload topic
      // when the feature is enabled. FCM topic subscriptions are bound to the
      // registration token (which rotates per session), so we (re)subscribe on
      // every boot. The flag can also be switched on mid-session, in which case
      // the Feature Flags page calls subscribeToPhotoUploadTopic directly.
      if (getStoredFeatureFlag('push_parking_photo')) {
        await subscribeToPhotoUploadTopic()
      } else {
        console.log(
          `Skipping ${PHOTO_UPLOAD_TOPIC} topic subscription, push_parking_photo flag is off`,
        )
      }
    }
  } catch (err) {
    console.log('Notification registration id failed', err)
    if (err?.graphQLErrors) {
      const error = err as ApolloError
      const invalidDeviceToken = error.graphQLErrors.some(
        // @ts-expect-error extensions is just [string]: any
        e => e.extensions?.response?.status === 400,
      )

      if (invalidDeviceToken) {
        console.log('FCM device token is invalid deleting.')
        // remove token so we get a fresh one next time we boot the app.
        // This is an experiment to see if it reduces cases of: [ch11948]
        try {
          await deleteToken(messaging)
        } catch (err) {
          console.log('Failed to delete FCM device token', err)
        }
      }
    }
  }

  // register message handler once, this runs on every auth state change
  if (onMessageRegistered) {
    return
  }
  onMessageRegistered = true
  onMessage(messaging, async payload => {
    console.log('Message received. ', payload)

    // user claims were updated, let's force refresh the token
    if (payload?.data?.eventType === 'user.claims') {
      await auth.currentUser?.getIdToken(true)
    }

    // A parking photo was uploaded. This is a topic broadcast (not addressed to
    // this user specifically), so relay it into the React tree and let whichever
    // journey is on screen decide whether it's relevant.
    if (payload?.data?.eventType === PHOTO_UPLOAD_TOPIC) {
      window.dispatchEvent(
        new CustomEvent(PHOTO_UPLOAD_EVENT, {
          detail: { journeyId: payload.data.journey_id },
        }),
      )
    }
  })
}

// Must be called from a click handler: browsers only allow
// Notification.requestPermission() inside a user gesture.
export const enablePushNotifications = async () => {
  if (!('Notification' in window) || Notification.permission !== 'default') {
    return
  }
  const permission = await Notification.requestPermission()
  if (permission === 'granted') {
    await registerMessaging()
  } else {
    console.log(`Notification permission not granted (${permission}).`)
  }
}

export default registerMessaging
